// The places and units a guest can look for (VIS-227): one list for the Explore search (chrome/explore.js) and for
// "Where to?" (lazy/whereto.js), so both offer the same things and find them the same way. Pure: no page, no storage.
//   - every place and unit the page shows: what the preset hides (places.hidden; a hidden type or category is not in
//     the content at all) or keeps as scenery is not offered, the same rule as the list of places
//   - a unit by its number ("Aurora Cabin 12"), only where the map shows numbers (labels.unitNumbers)
//   - ranking: a number typed finds the unit with that number first, then words (rankDestinations)
import { placeForKey, placeLevel, CATEGORIES, UNIT_TYPES } from '../content.js';

/**
 * Every destination the page offers now: [{ key, kind: 'unit' | 'place', name, sub, to: { place }, img?, unit?, number? }]
 * (`img`: the place's photo, a unit's its type's, as the Explore list shows them).
 * @param o.keys    the map's place keys (api.places.keys(): a place the preset hides is not built there)
 * @param o.values  the resolved settings (runtime.get())
 * @param o.T, o.tx the translator's words and texts (i18n.js)
 * @param o.hidden  optional (key) => true for a place taken off the map live (the editor's frame)
 */
export function destinations({ keys, values = {}, T, tx, hidden }) {
  const numbers = values['labels.unitNumbers'] !== false, off = new Set(Array.isArray(values['places.hidden']) ? values['places.hidden'] : []), out = [];
  for (const key of keys) {
    const p = placeForKey(key);
    if (!p || placeLevel(key, values) === 'scenery' || off.has(key) || hidden?.(key)) continue;
    if (p.unit) {
      if (!numbers || p.number == null) continue; // a unit is told apart by its number: without numbers on the map there is no "Cabin 12"
      out.push({ key, kind: 'unit', unit: p.unit, number: p.number, name: T('unit.title', { name: tx(p.name), number: p.number }), sub: '', to: { place: key }, img: UNIT_TYPES[p.unit]?.image }); // its name says its type
    } else out.push({ key, kind: 'place', name: tx(p.title || p.name), sub: tx(CATEGORIES.find((c) => c.id === p.category)?.label), to: { place: key }, img: p.image });
  }
  return out.filter((d) => d.name);
}

/** Text as it is compared: lower case, accents off ("Mökki" = "mokki"), spaces folded. */
export const fold = (s) => String(s ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
const KIND = { place: 0, pin: 1, unit: 2 }; // the same match: a place first, a pin, then a unit (a unit is found by its number)

/**
 * The destinations that match `text`, best first, at most `max`: `items` [{ key, name, kind: 'unit' | 'place' | 'pin',
 * number? (a unit's number, where the map shows numbers) }]. A number typed finds the unit with that number first, then
 * the units whose number starts with it ("12" = Cabin 12, then 120...). Words: a name that starts with them, a word of
 * the name that starts with them, a name that has them anywhere. Empty text: nothing.
 */
export function rankDestinations(items, text, max = 6) {
  const q = fold(text);
  if (!q) return [];
  const num = /^\d+$/.test(q);
  const score = (it) => {
    const n = it.number == null ? '' : String(it.number), name = fold(it.name);
    if (num && n === q) return 0;
    if (num && n.startsWith(q)) return 1;
    if (name.startsWith(q)) return 2;
    if (name.split(/[\s\-–·,/()]+/).some((w) => w.startsWith(q))) return 3;
    return name.includes(q) ? 4 : -1;
  };
  return items.map((it) => ({ it, s: score(it) })).filter((x) => x.s >= 0)
    .sort((a, b) => a.s - b.s || (KIND[a.it.kind] ?? 3) - (KIND[b.it.kind] ?? 3) || (Number(a.it.number) || 0) - (Number(b.it.number) || 0) || fold(a.it.name).localeCompare(fold(b.it.name)))
    .slice(0, max).map((x) => x.it);
}
